import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { CommonModule } from "@angular/common";
import { EventQuotaInspect } from "../../../pojo/EventQuotaInspect";
import { S25ItemComponent } from "../../s25-item/s25.item.component";
import { Item } from "../../../pojo/Item";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25PipesModule } from "../../../pipes/s25.pipes.module";
import { UserStore } from "../../../stores/user.store";

@TypeManagerDecorator("s25-ng-event-quota-inspector-detail")
@Component({
    selector: "s25-ng-event-quota-inspector-detail",
    imports: [CommonModule, S25ItemComponent, S25PipesModule],
    template: `
        @if (occurrence(); as occurrenceRow) {
            <div class="quota-inspector-detail">
                @for (window of occurrenceRow.windows; track windowKey(window)) {
                    <div class="quota-inspector-detail__window">
                        <div class="quota-inspector-detail__window-summary">
                            <strong>Window:</strong>
                            {{ window.windowStart | dateFormat: userStore.dateTimeFormat() }} –
                            {{ window.windowEnd | dateFormat: userStore.dateTimeFormat() }}
                            @if (window.quotaMax) {
                                <span>
                                    · {{ window.rsrvCount ?? 0 }}/{{ window.quotaMax }}
                                    {{ countUnitLabel() }}
                                </span>
                            }
                            @if (window.durationMax) {
                                <span> · {{ window.durationTotal ?? 0 }}/{{ window.durationMax }} minutes </span>
                            }
                        </div>
                        @if (window.countedReservations?.length) {
                            <table class="quota-inspector-detail__table">
                                <thead>
                                    <tr>
                                        <th>Occurrence</th>
                                        <th>Event</th>
                                        <th>Start</th>
                                        <th>End</th>
                                        <th>Minutes</th>
                                        <th>Source</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    @for (countedRow of window.countedReservations; track countedKey(countedRow)) {
                                        <tr>
                                            <td>
                                                @if (countedRow.rsrvId > 0) {
                                                    {{ countedRow.rsrvId }}
                                                } @else {
                                                    Proposed
                                                }
                                            </td>
                                            <td>
                                                @if (countedRow.eventId) {
                                                    <s25-item
                                                        [itemId]="countedRow.eventId"
                                                        [itemTypeId]="itemIds.Event"
                                                        [itemName]="'Event ' + countedRow.eventId"
                                                    ></s25-item>
                                                } @else {
                                                    —
                                                }
                                            </td>
                                            <td>{{ countedRow.startDt | dateFormat: userStore.dateTimeFormat() }}</td>
                                            <td>{{ countedRow.endDt | dateFormat: userStore.dateTimeFormat() }}</td>
                                            <td>{{ countedRow.durationMinutes }}</td>
                                            <td>
                                                {{
                                                    countedRow.isCandidate
                                                        ? "Proposed occurrence"
                                                        : "Existing reservation"
                                                }}
                                            </td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        } @else {
                            <p class="ngFinePrint">No occurrences counted in this window.</p>
                        }
                    </div>
                } @empty {
                    <p class="ngFinePrint">No quotas apply to this occurrence.</p>
                }
            </div>
        }
    `,
    styles: `
        :host {
            display: block;
            padding: 0.75rem 0;
        }

        .quota-inspector-detail__window + .quota-inspector-detail__window {
            margin-top: 0.75rem;
            padding-top: 0.75rem;
            border-top: 1px solid var(--color--border);
        }

        .quota-inspector-detail__window-summary {
            margin-bottom: 0.5rem;
        }

        .quota-inspector-detail__table {
            width: 100%;
            border-collapse: collapse;
            font-size: 0.875rem;
        }

        .quota-inspector-detail__table th,
        .quota-inspector-detail__table td {
            border: 1px solid var(--color--border);
            padding: 0.35rem 0.5rem;
            text-align: left;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class S25EventQuotaInspectorDetailComponent {
    readonly occurrence = input.required<EventQuotaInspect.InspectQuotaOccurrence>();
    readonly countByEvent = input(false);
    readonly userStore = inject(UserStore);

    readonly itemIds = Item.Ids;

    readonly countUnitLabel = computed(() => (this.countByEvent() ? "events" : "occurrences"));

    windowKey(window: EventQuotaInspect.InspectWindow): string {
        return `${window.windowStart}-${window.objectId ?? "global"}`;
    }

    countedKey(countedRow: EventQuotaInspect.InspectCountedReservation): string {
        return `${countedRow.rsrvId}-${countedRow.startDt}-${countedRow.objectId ?? "global"}`;
    }
}
